import { keyBy } from "lodash";
import { getCurrentSocket } from "../socketManager";
import { TURBINE_LAST_UPDATE, TURBINE_STATUS_UPDATE } from "../constants";
import { LIVE_PARAMS_UPDATE } from "../constants";

let subscribed = [];
// identifier -> { turbine, from } for everything currently subscribed, kept
// around so a socket.io reconnect (see reconnectHandlerAttached below) can
// replay the "subscribe" emits - socket.io rooms are per-connection on the
// server (routes/index.listen.js's socket.on('subscribe', ...) does
// socket.join(queue)), so a dropped-then-restored transport leaves the
// client's `subscribed` dedup guard still populated (nothing ever calls
// unsubscribeFromLiveParams for a network blip) while the server has no
// memory of that room membership at all - without replaying "subscribe"
// here, the tab keeps its stale last-known values until a full page reload
// starts everything fresh.
let subscriptionInfo = {};
let reconnectHandlerAttached = false;

export const updateLiveParameters = (turbine, lastTimestamp, valuesObject) => ({
  type: LIVE_PARAMS_UPDATE,
  payload: {
    windTurbineId: turbine.id,
    data: {
      ...valuesObject,
      timestamp: lastTimestamp,
    },
  },
});

export const turbineLastUpdate = (turbine, timestamp) => ({
  type: TURBINE_LAST_UPDATE,
  payload: {
    windTurbineId: turbine.id,
    data: timestamp,
  },
});

// online = the bridge/controller itself is reachable over MQTT; plcOnline =
// its Modbus link to the turbine PLC (bridges only, null when not reported).
export const turbineStatusUpdate = (turbine, online, plcOnline = null) => ({
  type: TURBINE_STATUS_UPDATE,
  payload: {
    windTurbineId: turbine.id,
    data: online,
    plcOnline,
  },
});

export const subscribeToLiveParams =
  (windTurbines, fromLocation = "dashboard") =>
  async (dispatch) => {
    let lastTimestamp;

    const handleSocketMessage = (turbine, data) => {
      const shouldUpdate = data && data.timestamp !== lastTimestamp;

      if (shouldUpdate) {
        lastTimestamp = data.timestamp;

        const values = data.values.map((v) => ({
          ...v,
          id: v.id.substring(v.id.lastIndexOf(".") + 1).toLowerCase(),
        }));

        const valuesObj = keyBy(values, "id");

        dispatch(updateLiveParameters(turbine, lastTimestamp, valuesObj));
        // When this browser RECEIVED it, not the device's own timestamp:
        // the Timeout check compares lastUpdate with this browser's
        // Date.now(), so using the device clock made any skew between the
        // turbine board's RTC and this PC count as data delay (measured
        // 2026-10-06: boards 0.4-4.2s behind the server, PCs vary too).
        dispatch(turbineLastUpdate(turbine, Date.now()));
      }
    };

    const s = await getCurrentSocket();

    if (!reconnectHandlerAttached) {
      reconnectHandlerAttached = true;
      // Fires on the initial connect too (subscriptionInfo is empty then, so
      // it's a no-op) and on every reconnect after a dropped transport -
      // re-joins every room the server would otherwise have forgotten about.
      //
      // NOTE: "connect" is a reserved socket.io event name - socket.io-client
      // routes emit() calls for reserved names straight to local listeners
      // instead of onto the wire (see node_modules/socket.io-client/lib/
      // socket.js's emit(), events.hasOwnProperty(ev) branch), so
      // s.emit("connect", ...) never reaches the server at all. Calling it
      // from inside this very "connect" listener re-triggered the listener
      // itself synchronously and infinitely - that's what blew the stack.
      s.on("connect", () => {
        Object.keys(subscriptionInfo).forEach((identifier) => {
          const { turbine, from } = subscriptionInfo[identifier];
          s.emit("subscribe", turbine, "", from);
        });
      });
    }

    const fromElem = windTurbines[windTurbines.length - 1];

    let from = "";
    if (typeof fromElem !== "undefined" && fromElem.hasOwnProperty("from")) {
      from = fromElem.from;
      if (!fromElem.hasOwnProperty("id")) {
        windTurbines.pop();
      }
    }

    if (from.length == 0 && fromLocation) {
      from = fromLocation;
    }

    const handleStatusMessage = (turbine, status) => {
      dispatch(
        turbineStatusUpdate(
          turbine,
          !!(status && status.online),
          status && typeof status.plcOnline === "boolean" ? status.plcOnline : null
        )
      );
    };

    windTurbines.forEach((turbine) => {
      if (subscribed.indexOf(turbine.identifier) < 0) {
        s.emit("connect", turbine.identifier);
        s.emit("subscribe", turbine, "", from);
        s.on(`data-${turbine.identifier}`, (data) =>
          handleSocketMessage(turbine, data)
        );
        s.on(`status-${turbine.identifier}`, (status) =>
          handleStatusMessage(turbine, status)
        );
        subscribed.push(turbine.identifier);
        subscriptionInfo[turbine.identifier] = { turbine, from };
      }
    });
  };

export const unsubscribeFromLiveParams = (windTurbines) => async (dispatch) => {
  const s = await getCurrentSocket();

  Object.keys(windTurbines).forEach((turbineId) => {
    const turbine = windTurbines[turbineId];

    let from = "";
    if (turbine.hasOwnProperty("from")) {
      from = turbine.from;
      delete turbine.from;
    }
    if (subscribed.indexOf(turbine.identifier) >= 0) {
      s.off(`data-${turbine.identifier}`);
      s.off(`status-${turbine.identifier}`);
      s.emit("unsubscribe", turbine.identifier, "", from);
      s.emit("disconnect", turbine.identifier);
      subscribed = subscribed.filter((t) => t !== turbine.identifier);
      delete subscriptionInfo[turbine.identifier];
      console.log("---DISCONNECT FROM ", turbine.identifier, from, subscribed);
    }
  });
};
